/**
 * AnnotationPanel Component
 * Sidebar showing annotation list, details, and comment input
 */

import React, { useState } from 'react';
import { FiX, FiMessageSquare, FiSquare, FiCircle, FiCheck, FiClock, FiEye, FiTrash2 } from 'react-icons/fi';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-amber-500', bg: 'bg-amber-500/10', icon: FiClock },
  reviewed: { label: 'Reviewed', color: 'text-blue-500', bg: 'bg-blue-500/10', icon: FiEye },
  addressed: { label: 'Addressed', color: 'text-emerald-500', bg: 'bg-emerald-500/10', icon: FiCheck },
  dismissed: { label: 'Dismissed', color: 'text-gray-400', bg: 'bg-gray-400/10', icon: FiX }
};

function AnnotationItem({ annotation, isSelected, onSelect, onDelete, canDelete }) {
  const status = STATUS_CONFIG[annotation.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const ShapeIcon = annotation.shape_type === 'circle' ? FiCircle : FiSquare;

  return (
    <div
      onClick={() => onSelect(annotation.id)}
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected ? 'bg-white/20 ring-1 ring-white/40' : 'bg-white/5 hover:bg-white/10'
      }`}
    >
      <div className="flex items-start gap-2">
        <ShapeIcon className="w-4 h-4 mt-0.5 text-white/60" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${status.bg} ${status.color}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
            {annotation.client_name && (
              <span className="text-xs text-white/50 truncate">{annotation.client_name}</span>
            )}
          </div>
          {annotation.comment && (
            <p className="mt-1 text-sm text-white/80 line-clamp-2">{annotation.comment}</p>
          )}
          {annotation.responses && annotation.responses.length > 0 && (
            <div className="mt-1 flex items-center gap-1 text-xs text-white/50">
              <FiMessageSquare size={10} />
              {annotation.responses.length} response{annotation.responses.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        {canDelete && annotation.status === 'pending' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(annotation.id);
            }}
            className="p-1 text-white/40 hover:text-red-400 transition-colors"
            title="Delete annotation"
          >
            <FiTrash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function AnnotationDetail({ annotation, onClose }) {
  const status = STATUS_CONFIG[annotation.status] || STATUS_CONFIG.pending;

  return (
    <div className="p-3 bg-white/10 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${status.bg} ${status.color}`}>
          {status.label}
        </span>
        <button onClick={onClose} className="text-white/50 hover:text-white">
          <FiX size={16} />
        </button>
      </div>

      {annotation.comment && (
        <div className="mb-3">
          <p className="text-sm text-white/90">{annotation.comment}</p>
          <p className="text-xs text-white/40 mt-1">
            {annotation.client_name || 'Client'} - {new Date(annotation.created_at).toLocaleDateString()}
          </p>
        </div>
      )}

      {annotation.responses && annotation.responses.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-white/10">
          <p className="text-xs text-white/50 uppercase tracking-wide">Responses</p>
          {annotation.responses.map(response => (
            <div key={response.id} className="pl-2 border-l-2 border-blue-500/50">
              <p className="text-sm text-white/80">{response.response}</p>
              <p className="text-xs text-white/40 mt-0.5">
                {response.user_name || 'Photographer'} - {new Date(response.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnnotationPanel({
  annotations = [],
  selectedId = null,
  onSelect = () => {},
  onDelete = () => {},
  onAddComment = () => {},
  isOpen = false,
  onClose = () => {},
  canDelete = false,
  showCommentInput = true
}) {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedAnnotation = annotations.find(a => a.id === selectedId);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-80 bg-black/80 backdrop-blur-sm border-l border-white/10 flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-white font-medium">Annotations</h3>
        <button onClick={onClose} className="text-white/50 hover:text-white p-1">
          <FiX size={18} />
        </button>
      </div>

      {/* Annotation list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {annotations.length === 0 ? (
          <p className="text-center text-white/50 text-sm py-8">
            No annotations yet.<br />
            Use the tools to mark areas of interest.
          </p>
        ) : (
          annotations.map(annotation => (
            <AnnotationItem
              key={annotation.id}
              annotation={annotation}
              isSelected={annotation.id === selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              canDelete={canDelete}
            />
          ))
        )}
      </div>

      {/* Selected annotation detail */}
      {selectedAnnotation && (
        <div className="p-2 border-t border-white/10">
          <AnnotationDetail
            annotation={selectedAnnotation}
            onClose={() => onSelect(null)}
          />
        </div>
      )}

      {/* Comment input */}
      {showCommentInput && (
        <form onSubmit={handleSubmitComment} className="p-3 border-t border-white/10">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment to your annotation..."
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-white/40"
            rows={2}
          />
          <button
            type="submit"
            disabled={!newComment.trim() || isSubmitting}
            className="mt-2 w-full px-3 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Adding...' : 'Add Comment'}
          </button>
        </form>
      )}
    </div>
  );
}
